// @greenlight-spec: 27-fleet-health-monitor.md
// @greenlight-spec: 26-versioning-update-strategy.md#trial-and-entitlement-copy
// @greenlight-spec: decisions/0043-fleet-reporting-enforcement.md
import { z } from 'zod';
import { FleetReleaseValidationResponseSchema } from './fleet-release-validation.js';
/**
 * What Shift asserts about an install's license on the report response.
 *
 * A distinct vocabulary from `ENTITLEMENT_STATES` (types/billing.ts) on
 * purpose: that one is the install's internal enforcement vocabulary and
 * carries marketplace verdicts (`suspended`, `unsubscribed`) that never cross
 * this wire, while `grace` and `lapsed` are phases only a dated term can have.
 * The install maps this onto that.
 *
 * `grace` is still entitled — it warns, and names the day new work pauses.
 * Only `lapsed` gates, and `unknown` means Shift is not asserting anything.
 */
export const FLEET_ENTITLEMENT_STATES = ['entitled', 'grace', 'lapsed', 'unknown'];
/**
 * Why the state is what it is, so `/internal-health` can say more than
 * "lapsed" and an operator reading a support ticket can tell a trial that ran
 * out from terms nobody ever recorded. Closed set: the install renders an
 * unknown value as unrecognized rather than guessing.
 */
export const FLEET_ENTITLEMENT_REASONS = [
    'trial_active',
    'trial_ending',
    'trial_expired',
    'license_recorded',
    'no_terms_recorded',
    'installation_disabled',
    // Past the trial, an install whose billing account holds terms is licensed
    // by that account's normalized standing (docs/27 §Direct-contract billing).
    'payment_current',
    'manual_terms',
    'payment_past_due',
    'payment_failed',
    'standing_unknown',
    // A Stripe-collected account must hold a saved payment method within seven
    // days of its creation; these govern ahead of the trial while it does not.
    'payment_method_pending',
    'payment_method_due',
    'payment_method_missing',
    // A saved card is positive license evidence while nothing else gives a verdict.
    'payment_method_on_file',
    // A claimed install whose billing origin is `internal` is Shift's own.
    'internal_install',
];
/** The reasons whose remedy is saving a card in Settings → Billing, in every state. */
export const FLEET_PAYMENT_METHOD_REASONS = [
    'payment_method_pending',
    'payment_method_due',
    'payment_method_missing',
];
/**
 * The entitlement object on `POST /api/v1/reports`' `202` (docs/27 §API),
 * derived from the terms already recorded on the installation row.
 *
 * `effective_until` is the **term boundary** — the last day the install is
 * licensed — and is the same day in every state that has a term: `state` says
 * where the install sits relative to it. One date rather than a per-state one
 * is what lets `/internal-health` show IT its own trial end date before the
 * deadline matters, and keeps the boundary from being computed twice.
 * `null` means no fixed term (a recorded contract, or nothing recorded).
 */
export const FleetEntitlementSchema = z.object({
    state: z.enum(FLEET_ENTITLEMENT_STATES),
    /** UTC `YYYY-MM-DD`; `null` when there is no fixed term. */
    effective_until: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    reason: z.enum(FLEET_ENTITLEMENT_REASONS),
});
/** Stored when Shift names a `reason` this install's release predates. */
export const UNRECOGNIZED_ENTITLEMENT_REASON = 'unrecognized';
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
/** One length longer than this and it would not fit `fleet_entitlement_signals.reason`. */
const REASON_SHAPE = /^[a-z][a-z0-9_]{0,63}$/;
/**
 * What the **install** parses the same object with, and it is deliberately
 * looser than the emitter contract above.
 *
 * `state` and `reason` name values, not fields, so Shift can add one without a
 * schema version to signal it — the same problem docs/27 solves for the
 * updater-run vocabulary. A strict parse would reject the whole entitlement
 * over one unfamiliar word, and since an unreadable entitlement freezes
 * enforcement, a single added reason would quietly stop every older install in
 * the fleet from learning its license state.
 *
 * So an unfamiliar `state` degrades to `unknown` — the only safe reading of a
 * verdict this release cannot interpret — and an unfamiliar `reason` is kept
 * verbatim for the operator to read, bounded in shape and length because it
 * reaches a UI. What is *not* tolerated is a structurally broken object: that
 * leaves the install's stored entitlement untouched rather than overwriting a
 * real assertion with a guess.
 */
export const FleetEntitlementResponseSchema = z.object({
    // Each field must be *present* — tolerance is for unfamiliar values, not for
    // absent ones. A missing `state` that quietly became `unknown` would let a
    // malformed response overwrite a real assertion, which is the one thing this
    // must never do; instead the object fails to parse and the caller keeps what
    // it had.
    state: z
        .string()
        .transform((value) => FLEET_ENTITLEMENT_STATES.includes(value)
        ? value
        : 'unknown'),
    effective_until: z
        .union([z.string(), z.null()])
        .transform((value) => (value !== null && DATE_ONLY.test(value) ? value : null)),
    reason: z
        .string()
        .transform((value) => (REASON_SHAPE.test(value) ? value : UNRECOGNIZED_ENTITLEMENT_REASON)),
});
/**
 * The `202` body as the install reads it. `entitlement` is optional in both
 * directions: a fleet monitor that does not send it leaves the install's stored
 * state exactly as it was, and an install that predates the field ignores it —
 * so neither side has to deploy before the other (docs/27 §API version policy).
 *
 * Parsed non-strictly on purpose. A later field on this response must not make
 * an older install treat a successful delivery as a failure, so unknown keys
 * are dropped rather than rejected.
 */
export const FleetReportAcceptedSchema = z.object({
    status: z.literal('accepted'),
    report_id: z.string(),
    entitlement: FleetEntitlementResponseSchema.optional(),
    release_validation: FleetReleaseValidationResponseSchema,
});
/**
 * API projection of the install's `fleet_entitlement_signals` row for
 * `/internal-health`: what Shift last asserted about this install's license,
 * and when the install received it.
 *
 * `received_at` is here because the assertion alone is not the whole story —
 * an install that stopped hearing from Shift keeps showing its last known
 * license state (nothing ages a signal into a lapse), so IT needs to see how
 * old the answer is to read it correctly.
 */
export const FleetEntitlementSignalSchema = z.object({
    state: z.enum(FLEET_ENTITLEMENT_STATES),
    /** The last day this install is licensed; `null` when there is no fixed term. */
    effective_until: z.string().nullable(),
    reason: z.string(),
    received_at: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=fleet-entitlement.js.map